// Consulenza.js — versione traducibile con i18next
import React, { useEffect, useMemo } from "react";
import { Container, Row, Col, Card, Button, Accordion } from "react-bootstrap";
import { Parallax } from "react-parallax";
import { FaClipboardCheck, FaCogs, FaShieldAlt } from "react-icons/fa";
import AOS from "aos";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import "aos/dist/aos.css";
import "./Consulenza.css";

export default function Consulenza() {
  const { t } = useTranslation();

  useEffect(() => {
    AOS.init({ duration: 900, once: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Structured Data FAQPage per SEO
  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": t("consulenza.faq1Q"),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t("consulenza.faq1A")
        }
      },
      {
        "@type": "Question",
        "name": t("consulenza.faq2Q"),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t("consulenza.faq2A")
        }
      },
      {
        "@type": "Question",
        "name": t("consulenza.faq3Q"),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t("consulenza.faq3A")
        }
      }
    ]
  }), [t]);

  return (
    <>
      <Helmet>
        <title>Consulenza IT | Andromeda Programmazione - Supporto Tecnologico</title>
        <meta 
          name="description" 
          content="Servizio di consulenza IT professionale. Analisi delle necessità, orientamento tecnico e supporto per progetti digitali. Consulenza personalizzata per aziende, startup e professionisti. Richiedi una consulenza gratuita." 
        />
        <meta 
          name="keywords" 
          content="consulenza IT, consulenza informatica, consulenza software, consulenza tecnologica, orientamento tecnico, supporto IT, consulenza digitale Lecce, consulenza web development" 
        />
        <link rel="canonical" href="https://www.andromedaprog.it/consulenza" />
        
        <meta property="og:title" content="Consulenza IT | Andromeda Programmazione" />
        <meta 
          property="og:description" 
          content="Consulenza IT professionale per aziende e professionisti. Analisi, orientamento tecnico e supporto per progetti digitali su misura." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.andromedaprog.it/consulenza" />
        <meta property="og:image" content="https://www.andromedaprog.it/images/hero-bg.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Consulenza IT | Andromeda Programmazione" />
        <meta 
          name="twitter:description" 
          content="Consulenza IT professionale per progetti digitali. Analisi, orientamento e supporto tecnologico." 
        />
        
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      </Helmet>
      
      <div className="consulenza-page">
      {/* HERO */}
      <Parallax
        bgImage={"/images/consulenza-hero.jpg"}
        strength={400}
        className="hero-parallax"
      >
        <div className="hero-consulenza">
          <h1 className="hero-title">{t("consulenza.heroTitle")}</h1>
          <p className="hero-subtitle">{t("consulenza.heroSubtitle")}</p>
        </div>
      </Parallax>

      <Container>
        {/* SEZIONE A – Cosa Offriamo */}
        <section className="cards-container" data-aos="fade-up">
          <Row>
            <Col md={4}>
              <Card className="card-consulenza">
                <FaClipboardCheck className="icon-consulenza" />
                <h3>{t("consulenza.analisiTitolo")}</h3>
                <p>{t("consulenza.analisiDesc")}</p>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="card-consulenza">
                <FaCogs className="icon-consulenza" />
                <h3>{t("consulenza.orientamentoTitolo")}</h3>
                <p>{t("consulenza.orientamentoDesc")}</p>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="card-consulenza">
                <FaShieldAlt className="icon-consulenza" />
                <h3>{t("consulenza.supportoTitolo")}</h3>
                <p>{t("consulenza.supportoDesc")}</p>
              </Card>
            </Col>
          </Row>
        </section>

        {/* SEZIONE B – A chi è rivolta */}
        <section className="info-section" data-aos="fade-up">
          <h2 className="section-title">{t("consulenza.aChiE")}</h2>
          <p className="info-text">{t("consulenza.aChiEText")}</p>
          <ul className="info-list">
            <li>✔️ {t("consulenza.item1")}</li>
            <li>✔️ {t("consulenza.item2")}</li>
            <li>✔️ {t("consulenza.item3")}</li>
            <li>✔️ {t("consulenza.item4")}</li>
          </ul>
        </section>

        {/* SEZIONE D – Perché fidarti di noi */}
        <section className="info-section" data-aos="fade-up">
          <h2 className="section-title">{t("consulenza.percheFidarsi")}</h2>
          <ul className="info-list">
            <li>⭐ {t("consulenza.fiducia1")}</li>
            <li>⭐ {t("consulenza.fiducia2")}</li>
            <li>⭐ {t("consulenza.fiducia3")}</li>
            <li>⭐ {t("consulenza.fiducia4")}</li>
          </ul>
        </section>

        {/* FAQ */}
        <section className="faq-section" data-aos="fade-up">
          <h2 className="section-title">{t("consulenza.faqTitle")}</h2>
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>{t("consulenza.faq1Q")}</Accordion.Header>
              <Accordion.Body>{t("consulenza.faq1A")}</Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>{t("consulenza.faq2Q")}</Accordion.Header>
              <Accordion.Body>{t("consulenza.faq2A")}</Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>{t("consulenza.faq3Q")}</Accordion.Header>
              <Accordion.Body>{t("consulenza.faq3A")}</Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </section>

        {/* CALL TO ACTION */}
        <div className="cta-box" data-aos="fade-up">
          <Button
            className="cta-btn"
            size="lg"
            href="mailto:info@andromedaprog.it?subject=Richiesta%20Servizio"
          >
            {t("consulenza.cta")}
          </Button>
        </div>
      </Container>
      </div>
    </>
  );
}
